import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { Expression } from '@/models/audiences';
import { Rule } from '@/models/rules';
import { audiencerateApi } from '@/services/audiencerate';
import { DatasourceTraitDistributions, GetCustomPropertyData } from '@/services/dataSources';
import { MarketIntelligenceStrategicActionApi } from '@/services/endpoints/marketIntelligence';

export const AUDIENCE_PREFILL_TIMEOUT_MS = 120_000;

export type AudiencePrefillRequestBody = {
  action: MarketIntelligenceStrategicActionApi;
  traitDistributions: DatasourceTraitDistributions[];
  customProperties: GetCustomPropertyData;
  datasourceRules: Rule[];
};

export type AudiencePrefillResponse = {
  name: string;
  matchingTrait: string;
  expression: Record<string, Expression> | null;
};

export type AudiencePrefillArgs = {
  workspaceId: string;
  action: MarketIntelligenceStrategicActionApi;
  signal?: AbortSignal;
};

const isAbortError = (error: unknown) => {
  if (!error || typeof error !== 'object') {
    return false;
  }

  return 'name' in error && (error as { name?: string }).name === 'AbortError';
};

export const audiencePrefillApi = audiencerateApi.injectEndpoints({
  endpoints: (build) => ({
    audiencePrefill: build.mutation<AudiencePrefillResponse, AudiencePrefillArgs>({
      async queryFn({ workspaceId, action, signal }, _api, _extraOptions, baseQuery) {
        const datasourceIds = action.suggestedDatasourceIds;

        const fetchWithSignal = async <T,>(args: Parameters<typeof baseQuery>[0]) => {
          const requestArgs = typeof args === 'string' ? { url: args, signal } : { ...args, signal };
          return baseQuery(requestArgs) as Promise<{ data?: T; error?: FetchBaseQueryError }>;
        };

        try {
          const [traitDistributionsResult, customPropertiesResult, viewRulesResult, trackRulesResult] = await Promise.all([
            fetchWithSignal<DatasourceTraitDistributions[]>({
              url: `/v1/workspaces/${workspaceId}/datasources/trait-distributions`,
              params: { datasourceIds },
            }),
            fetchWithSignal<GetCustomPropertyData>({
              url: `/v1/workspaces/${workspaceId}/datasources/custom-properties`,
              params: { datasourceIds },
            }),
            fetchWithSignal<Rule[]>({
              url: `/v1/workspaces/${workspaceId}/datasourcesrules`,
              params: { type: 'VIEW', datasourceIds: datasourceIds.join(',') },
            }),
            fetchWithSignal<Rule[]>({
              url: `/v1/workspaces/${workspaceId}/datasourcesrules`,
              params: { type: 'TRACK', datasourceIds: datasourceIds.join(',') },
            }),
          ]);

          const upstreamError =
            traitDistributionsResult.error || customPropertiesResult.error || viewRulesResult.error || trackRulesResult.error;

          if (upstreamError) {
            return { error: upstreamError };
          }

          const body: AudiencePrefillRequestBody = {
            action,
            traitDistributions: traitDistributionsResult.data ?? [],
            customProperties: customPropertiesResult.data ?? { properties: [] },
            datasourceRules: [...(viewRulesResult.data ?? []), ...(trackRulesResult.data ?? [])],
          };

          const prefillResult = await fetchWithSignal<AudiencePrefillResponse>({
            url: `/v1/workspaces/${workspaceId}/audience-prefill`,
            method: 'POST',
            body,
          });

          if (prefillResult.error) {
            return { error: prefillResult.error };
          }

          if (!prefillResult.data) {
            return { error: { status: 'CUSTOM_ERROR', error: 'Empty audience-prefill response' } };
          }

          return { data: prefillResult.data };
        } catch (error) {
          if (isAbortError(error) || signal?.aborted) {
            return { error: { status: 'CUSTOM_ERROR', error: 'Audience prefill aborted or timed out' } };
          }

          return { error: { status: 'FETCH_ERROR', error: String(error) } };
        }
      },
    }),
  }),
  overrideExisting: false,
});

export const { useAudiencePrefillMutation } = audiencePrefillApi;
